import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Eye, X, ChevronLeft, Shield, Calendar, User, Clock, FolderOpen, Image, File, AlertCircle, CheckCircle2, Plus, Layers, Loader2, Sparkles, BadgeCheck, MessageSquare, RotateCcw } from 'lucide-react';
import { auditReports } from '../services/api';
import { getCurrentUser } from '../utils/patientAccess';
import AuditReportBody from './audit/AuditReportBody';

// Stages of the automated audit, in order, as the backend reports them.
const RUN_STAGES = [
  { key: 'preparing', label: 'Preparando documentos' },
  { key: 'reading', label: 'Lendo documentos' },
  { key: 'analyzing', label: 'Cruzando informações' },
  { key: 'verifying', label: 'Verificando achados' },
  { key: 'generating', label: 'Gerando relatório' }
];
const RUN_POLL_MS = 3000;

const AuditReportsTab = ({ patientId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);


  // Clinic documents (saved on the server) and the automated audit run
  const [clinicDocs, setClinicDocs] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [auditRun, setAuditRun] = useState(null);
  const [validating, setValidating] = useState(false);
  const fileInputRef = useRef(null);

  // Uploading, generating and validating audits is for admins and supervisors.
  const currentUser = getCurrentUser();
  const canAudit = ['admin', 'supervisor', 'super_admin'].includes(currentUser?.role);
  const runActive = auditRun && ['queued', 'running'].includes(auditRun.status);

  const DISCIPLINES = [
    { key: 'medico',    label: 'Médico',           color: '#2563eb', bg: '#eff6ff', icon: '🩺' },
    { key: 'fono',      label: 'Fonoaudiologia',   color: '#7c3aed', bg: '#f5f3ff', icon: '🗣️' },
    { key: 'psico',     label: 'Psicologia',       color: '#0891b2', bg: '#ecfeff', icon: '🧠' },
    { key: 'psicoped',  label: 'Psicopedagogia',   color: '#c2410c', bg: '#fff7ed', icon: '📚' },
    { key: 'to',        label: 'Terapia Ocupacional', color: '#16a34a', bg: '#f0fdf4', icon: '🤲' },
    { key: 'outro',     label: 'Outro',            color: '#64748b', bg: '#f8fafc', icon: '📄' },
  ];

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const apiError = (err, fallback) => err?.response?.data?.message || fallback;

  const fetchClinicDocs = useCallback(async () => {
    if (!canAudit) return;
    try {
      const response = await auditReports.listDocuments(patientId);
      if (response.data.status === 'success') setClinicDocs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching clinic documents:', err);
    }
  }, [patientId, canAudit]);

  const handleClinicFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (runActive) return;
    const files = Array.from(e.dataTransfer?.files || []);
    addClinicFiles(files);
  };

  const handleClinicFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    addClinicFiles(files);
    e.target.value = '';
  };

  const addClinicFiles = async (files) => {
    const accepted = files.filter(f => /\.(jpe?g|png|gif|webp|bmp|tiff?|pdf|docx)$/i.test(f.name));
    const skipped = files.filter(f => !accepted.includes(f));
    if (skipped.length) {
      setError(`Formato não aceito: ${skipped.map(f => f.name).join(', ')}. Envie PDF, DOCX ou imagens (JPG, PNG, TIFF).`);
    }
    if (!accepted.length) return;

    const formData = new FormData();
    accepted.forEach(f => formData.append('files', f));
    formData.append('disciplines', JSON.stringify(accepted.map(f => guessDiscipline(f.name))));
    try {
      setUploadingDocs(accepted.length);
      const response = await auditReports.uploadDocuments(patientId, formData);
      setClinicDocs(prev => [...prev, ...(response.data.data || [])]);
    } catch (err) {
      console.error('Error uploading clinic documents:', err);
      setError(apiError(err, 'Erro ao enviar documentos clínicos.'));
    } finally {
      setUploadingDocs(0);
    }
  };

  const guessDiscipline = (filename) => {
    const lower = filename.toLowerCase();
    if (lower.includes('medic') || lower.includes('médic') || lower.includes('neuro') || lower.includes('laudo')) return 'medico';
    if (lower.includes('fono')) return 'fono';
    if (lower.includes('psicoped')) return 'psicoped';
    if (lower.includes('psico') || lower.includes('psicol') || lower.includes('aba')) return 'psico';
    if (/(^|[^a-z])to([^a-z]|$)/.test(lower) || lower.includes('ocupacion')) return 'to';
    return 'outro';
  };

  const updateDocDiscipline = async (docId, discipline) => {
    const previous = clinicDocs;
    setClinicDocs(prev => prev.map(d => d.id === docId ? { ...d, discipline } : d));
    try {
      await auditReports.updateDocument(docId, discipline);
    } catch (err) {
      setClinicDocs(previous);
      setError(apiError(err, 'Erro ao alterar a disciplina do documento.'));
    }
  };

  const removeClinicDoc = async (docId) => {
    try {
      await auditReports.deleteDocument(docId);
      setClinicDocs(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      setError(apiError(err, 'Erro ao remover o documento.'));
    }
  };

  const removeAllClinicDocs = async () => {
    if (!window.confirm('Remover todos os documentos clínicos deste paciente?')) return;
    for (const doc of clinicDocs) {
      await removeClinicDoc(doc.id);
    }
  };

  const filteredDocs = selectedDiscipline === 'all' ? clinicDocs : clinicDocs.filter(d => d.discipline === selectedDiscipline);

  const docCountByDiscipline = DISCIPLINES.map(d => ({
    ...d,
    count: clinicDocs.filter(doc => doc.discipline === d.key).length
  }));

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await auditReports.getByPatient(patientId);
      setReports(response.data.data || []);
    } catch (err) {
      console.error('Error fetching audit reports:', err);
      setError('Erro ao carregar relatórios de auditoria.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Clinic documents and the latest run (a run may still be going from before a reload)
  useEffect(() => {
    fetchClinicDocs();
    if (!canAudit) return;
    auditReports.getLatestRun(patientId)
      .then(res => { if (res.data.status === 'success') setAuditRun(res.data.data); })
      .catch(err => console.error('Error fetching latest audit run:', err));
  }, [patientId, canAudit, fetchClinicDocs]);

  // Poll an active run until it finishes
  useEffect(() => {
    if (!runActive) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await auditReports.getRun(auditRun.id);
        if (res.data.status !== 'success') return;
        const run = res.data.data;
        setAuditRun(run);
        if (run.status === 'completed') {
          await fetchReports();
          if (run.reportId) handleViewReport(run.reportId);
        }
      } catch (err) {
        console.error('Error polling audit run:', err);
      }
    }, RUN_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runActive, auditRun?.id, fetchReports]);

  const handleStartAudit = async () => {
    try {
      setError(null);
      const res = await auditReports.startRun(patientId, clinicDocs.map(d => d.id));
      setAuditRun(res.data.data);
    } catch (err) {
      console.error('Error starting audit:', err);
      if (err?.response?.status === 409 && err.response.data?.data) setAuditRun(err.response.data.data);
      setError(apiError(err, 'Erro ao iniciar a auditoria.'));
    }
  };

  const handleValidate = async () => {
    if (!selectedReport) return;
    try {
      setValidating(true);
      await auditReports.validate(selectedReport.id);
      await handleViewReport(selectedReport.id);
      await fetchReports();
    } catch (err) {
      console.error('Error validating report:', err);
      setError(apiError(err, 'Erro ao validar o relatório.'));
    } finally {
      setValidating(false);
    }
  };

  const handleReopen = async () => {
    if (!selectedReport) return;
    if (!window.confirm('Reabrir o relatório para edição? Ele voltará a ficar pendente de validação.')) return;
    try {
      setValidating(true);
      await auditReports.reopen(selectedReport.id);
      await handleViewReport(selectedReport.id);
      await fetchReports();
    } catch (err) {
      console.error('Error reopening report:', err);
      setError(apiError(err, 'Erro ao reabrir o relatório.'));
    } finally {
      setValidating(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      setLoadingReport(true);
      const response = await auditReports.getById(reportId);
      setSelectedReport(response.data.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Erro ao carregar relatório.');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      const response = await auditReports.download(report.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.originalFilename || 'audit-report.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Erro ao baixar relatório.');
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await auditReports.delete(reportId);
      setDeleteConfirm(null);
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
      await fetchReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Erro ao excluir relatório.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    // A date-only value ("2026-09-30") must not go through new Date(): that reads
    // it as UTC midnight and shows the previous day in Brazil.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Post-process HTML to color-code severity labels from the audit prompt
  const colorCodeSeverity = (html) => {
    if (!html) return html;

    // Severity badge definitions — order matters (longest match first)
    const severities = [
      { pattern: /CRÍTICO/gi,                         bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', dot: '#dc2626', label: 'CRÍTICO' },
      { pattern: /MÉDIO[\s\u00a0-]*ALTO/gi,           bg: '#fffbeb', border: '#fcd34d', color: '#92400e', dot: '#f59e0b', label: 'MÉDIO-ALTO' },
      { pattern: /ALTO/gi,                            bg: '#fff7ed', border: '#fdba74', color: '#9a3412', dot: '#f97316', label: 'ALTO' },
      { pattern: /MÉDIO/gi,                           bg: '#fffbeb', border: '#fcd34d', color: '#92400e', dot: '#eab308', label: 'MÉDIO' },
      { pattern: /BAIXO[\s\u00a0—–-]*pendente de verificação/gi, bg: '#f0fdf4', border: '#86efac', color: '#166534', dot: '#22c55e', label: 'BAIXO — pendente de verificação' },
      { pattern: /BAIXO/gi,                           bg: '#f0fdf4', border: '#86efac', color: '#166534', dot: '#22c55e', label: 'BAIXO' },
      { pattern: /PENDENTE DE ACOMPANHAMENTO/gi,      bg: '#f0fdf4', border: '#86efac', color: '#166534', dot: '#22c55e', label: 'PENDENTE DE ACOMPANHAMENTO' },
      { pattern: /PENDENTE DE VERIFICAÇÃO/gi,         bg: '#f0fdf4', border: '#86efac', color: '#166534', dot: '#22c55e', label: 'PENDENTE DE VERIFICAÇÃO' },
    ];

    const badge = (s) =>
      `<span style="display:inline-flex;align-items:center;gap:5px;padding:2px 10px 2px 8px;border-radius:6px;` +
      `font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:12px;font-weight:700;letter-spacing:0.04em;` +
      `white-space:nowrap;background:${s.bg};border:1px solid ${s.border};color:${s.color}">` +
      `<span style="width:7px;height:7px;border-radius:50%;background:${s.dot};flex-shrink:0"></span>${s.label}</span>`;

    // Replace severity keywords — only when they appear as standalone labels
    // (after "Severidade:" or as standalone bold/strong text)
    let result = html;

    // First pass: handle "Severidade: LABEL" pattern (with or without surrounding tags)
    for (const s of severities) {
      // Match Severidade: followed by optional tags and the severity label
      const sevPattern = new RegExp(
        `(Severidade\\s*:\\s*(?:<\\/?(strong|b|em|span)[^>]*>\\s*)*)` + s.pattern.source,
        'gi'
      );
      result = result.replace(sevPattern, (match, prefix) => {
        return prefix + badge(s);
      });
    }

    // Second pass: standalone severity labels inside <strong> or <b> tags that weren't already badged
    for (const s of severities) {
      const strongPattern = new RegExp(
        `(<(?:strong|b)>\\s*)` + s.pattern.source + `(\\s*<\\/(?:strong|b)>)`,
        'gi'
      );
      result = result.replace(strongPattern, (match, open, close) => {
        // Skip if already badged
        if (match.includes('border-radius:6px')) return match;
        return open + badge(s) + close;
      });
    }

    return result;
  };

  // ─── Detail / Preview View ───────────────────────────────────────────
  if (selectedReport) {
    return (
      <div style={{ background: '#f0f2f5', margin: '-1.5rem', padding: '1.5rem', minHeight: '100vh' }}>
        {/* Floating toolbar */}
        <div
          className="sticky top-0 z-20 flex items-center justify-between px-5 py-2.5"
          style={{
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '12px',
            marginBottom: '1.25rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
          }}
        >
          <button
            onClick={() => setSelectedReport(null)}
            className="flex items-center text-gray-500 hover:text-gray-800 font-medium transition-colors text-sm"
          >
            <ChevronLeft size={16} className="mr-1" />
            Todos os Relatórios
          </button>
          <div className="flex items-center gap-2.5">
            <span className="hidden sm:inline text-xs text-gray-400 font-mono bg-gray-100 px-2.5 py-1 rounded-md">
              {selectedReport.originalFilename}
            </span>
            <button
              onClick={() => handleDownload(selectedReport)}
              className="flex items-center px-4 py-2 text-white rounded-lg text-sm font-medium shadow-sm transition-all hover:shadow-md"
              style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
            >
              <Download size={14} className="mr-1.5" />
              Baixar Original
            </button>
          </div>
        </div>

        {/* Supervisor validation of AI-generated reports */}
        {selectedReport.source === 'ai_generated' && (
          <div className="flex justify-center mb-4">
            <div className="w-full flex items-center justify-between gap-4 px-5 py-3 rounded-xl" style={{
              maxWidth: '860px',
              background: selectedReport.validatedAt ? '#f0fdf4' : '#fffbeb',
              border: `1px solid ${selectedReport.validatedAt ? '#bbf7d0' : '#fde68a'}`
            }}>
              {selectedReport.validatedAt ? (
                <>
                  <span className="flex items-center gap-2 text-sm text-green-800">
                    <BadgeCheck size={16} />
                    Validado por {selectedReport.validator ? `${selectedReport.validator.firstName} ${selectedReport.validator.lastName}` : 'supervisor'} em {formatDateTime(selectedReport.validatedAt)}
                  </span>
                  {selectedReport.permissions?.canReopen && (
                    <button
                      onClick={handleReopen}
                      disabled={validating}
                      className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-green-800 bg-white border border-green-200 hover:bg-green-50 disabled:opacity-50"
                    >
                      {validating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                      Reabrir para edição
                    </button>
                  )}
                </>
              ) : (
                <>
                  <span className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    Relatório gerado pelo GiA, pendente de validação. Revise os achados antes de qualquer comunicação com a clínica.
                  </span>
                  {selectedReport.permissions?.canValidate && (
                    <button
                      onClick={handleValidate}
                      disabled={validating}
                      className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {validating ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
                      {validating ? 'Validando...' : 'Validar relatório'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Document page */}
        {selectedReport.htmlContent ? (
          <div className="flex justify-center">
            <div
              style={{
                width: '100%',
                maxWidth: '860px',
                background: '#fff',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}
            >
              {/* Confidential strip */}
              <div style={{
                background: '#0f172a',
                padding: '6px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Shield size={11} style={{ color: '#38bdf8' }} />
                  <span style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                    Documento Confidencial
                  </span>
                </div>
                <span style={{ color: '#475569', fontSize: '10px', fontFamily: 'monospace' }}>
                  ID: {selectedReport.id?.slice(0, 8).toUpperCase()}
                </span>
              </div>

              {/* Cover header */}
              <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%)',
                padding: '48px 48px 40px 48px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decorative elements */}
                <div style={{
                  position: 'absolute', top: 0, right: 0, width: '300px', height: '300px',
                  background: 'radial-gradient(circle at center, rgba(59,130,246,0.08) 0%, transparent 70%)',
                  transform: 'translate(30%, -30%)'
                }} />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, width: '200px', height: '200px',
                  background: 'radial-gradient(circle at center, rgba(14,165,233,0.06) 0%, transparent 70%)',
                  transform: 'translate(-30%, 30%)'
                }} />

                {/* Brand line */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', position: 'relative' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, #3b82f6, #0ea5e9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
                  }}>
                    <Shield size={16} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <span style={{ color: '#cbd5e1', fontSize: '11px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' }}>
                      Gia Clinical Platform
                    </span>
                  </div>
                </div>

                {/* Document type label */}
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(59,130,246,0.15)',
                  border: '1px solid rgba(59,130,246,0.25)',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  marginBottom: '16px',
                  position: 'relative'
                }}>
                  <span style={{ color: '#93c5fd', fontSize: '11px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Relatório de Auditoria QA
                  </span>
                </div>

                {/* Title */}
                <h1 style={{
                  color: '#f8fafc', fontSize: '26px', fontWeight: 700, lineHeight: 1.25,
                  margin: '0 0 8px 0', position: 'relative',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}>
                  {selectedReport.title}
                </h1>

                {selectedReport.comments && (
                  <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.5, margin: '0 0 24px 0', maxWidth: '560px', position: 'relative' }}>
                    {selectedReport.comments}
                  </p>
                )}

                {/* Metadata grid */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '1px', background: 'rgba(255,255,255,0.06)', borderRadius: '10px',
                  overflow: 'hidden', marginTop: '20px', position: 'relative'
                }}>
                  {[
                    { icon: Calendar, label: 'Data do Relatório', value: formatDate(selectedReport.reportDate) },
                    { icon: User, label: 'Responsável', value: selectedReport.uploader ? `${selectedReport.uploader.firstName} ${selectedReport.uploader.lastName}` : '—' },
                    { icon: Clock, label: 'Data de Envio', value: formatDateTime(selectedReport.created_at) },
                  ].map((item, i) => (
                    <div key={i} style={{ background: 'rgba(15,23,42,0.6)', padding: '14px 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <item.icon size={12} style={{ color: '#64748b' }} />
                        <span style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                          {item.label}
                        </span>
                      </div>
                      <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 500 }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accent line */}
              <div style={{ height: '3px', background: 'linear-gradient(90deg, #3b82f6 0%, #0ea5e9 40%, #e2e8f0 100%)' }} />

              {/* Document body */}
              <div style={{ padding: '40px 48px 48px 48px' }}>
                <style>{`
                  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400&display=swap');

                  .audit-doc-v2 {
                    font-family: 'Source Serif 4', 'Georgia', 'Cambria', 'Times New Roman', serif;
                    color: #1e293b;
                    font-size: 15px;
                    line-height: 1.8;
                  }

                  /* ── Headings ── */
                  .audit-doc-v2 h1 {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    font-size: 1.35rem;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 2.5rem 0 1rem 0;
                    padding-bottom: 0.6rem;
                    border-bottom: 2px solid #e2e8f0;
                    line-height: 1.3;
                    letter-spacing: -0.01em;
                    position: relative;
                  }
                  .audit-doc-v2 h1::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 48px;
                    height: 2px;
                    background: #3b82f6;
                  }
                  .audit-doc-v2 h1:first-child { margin-top: 0; }

                  .audit-doc-v2 h1.doc-title {
                    font-size: 1.6rem;
                    text-align: center;
                    border-bottom: none;
                    margin-bottom: 0.5rem;
                  }
                  .audit-doc-v2 h1.doc-title::after { display: none; }

                  .audit-doc-v2 h2 {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    font-size: 1.15rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 2rem 0 0.6rem 0;
                    line-height: 1.35;
                    letter-spacing: -0.005em;
                  }
                  .audit-doc-v2 h2.doc-subtitle {
                    text-align: center;
                    color: #64748b;
                    font-weight: 400;
                    font-family: 'Source Serif 4', Georgia, serif;
                    font-style: italic;
                    font-size: 1.05rem;
                    margin-top: -0.25rem;
                    margin-bottom: 2rem;
                  }

                  .audit-doc-v2 h3 {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #334155;
                    margin: 1.75rem 0 0.4rem 0;
                    line-height: 1.4;
                  }

                  .audit-doc-v2 h4 {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #475569;
                    margin: 1.5rem 0 0.35rem 0;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                  }

                  /* ── Body text ── */
                  .audit-doc-v2 p {
                    margin: 0 0 0.9rem 0;
                    color: #334155;
                  }

                  .audit-doc-v2 strong, .audit-doc-v2 b {
                    font-weight: 700;
                    color: #0f172a;
                  }

                  .audit-doc-v2 em, .audit-doc-v2 i {
                    font-style: italic;
                    color: #475569;
                  }

                  /* ── Lists ── */
                  .audit-doc-v2 ul {
                    margin: 0.5rem 0 1.25rem 0;
                    padding-left: 0;
                    list-style: none;
                  }
                  .audit-doc-v2 ul li {
                    position: relative;
                    padding-left: 1.4rem;
                    margin-bottom: 0.5rem;
                    color: #334155;
                  }
                  .audit-doc-v2 ul li::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0.65em;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #3b82f6;
                  }

                  .audit-doc-v2 ol {
                    margin: 0.5rem 0 1.25rem 0;
                    padding-left: 1.5rem;
                    counter-reset: item;
                    list-style: none;
                  }
                  .audit-doc-v2 ol li {
                    position: relative;
                    padding-left: 0.5rem;
                    margin-bottom: 0.5rem;
                    color: #334155;
                    counter-increment: item;
                  }
                  .audit-doc-v2 ol li::before {
                    content: counter(item);
                    position: absolute;
                    left: -1.6rem;
                    top: 0.12em;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #eff6ff;
                    color: #2563eb;
                    font-family: 'Inter', sans-serif;
                    font-size: 11px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    line-height: 1;
                  }

                  /* ── Tables ── */
                  .audit-doc-v2 table {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                    margin: 1.25rem 0 1.75rem 0;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    font-size: 13px;
                    line-height: 1.5;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
                  }
                  .audit-doc-v2 thead tr {
                    background: #f8fafc;
                  }
                  .audit-doc-v2 th {
                    padding: 10px 16px;
                    text-align: left;
                    font-weight: 600;
                    color: #1e293b;
                    border-bottom: 2px solid #e2e8f0;
                    white-space: nowrap;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                  }
                  .audit-doc-v2 td {
                    padding: 10px 16px;
                    color: #334155;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: top;
                  }
                  .audit-doc-v2 tbody tr:nth-child(even) {
                    background: #f8fafc;
                  }
                  .audit-doc-v2 tbody tr:last-child td {
                    border-bottom: none;
                  }
                  .audit-doc-v2 tbody tr:hover {
                    background: #eff6ff;
                  }

                  /* ── Blockquotes ── */
                  .audit-doc-v2 blockquote {
                    margin: 1.25rem 0;
                    padding: 16px 20px 16px 20px;
                    border: 1px solid #e0e7ff;
                    border-left: 4px solid #6366f1;
                    background: #f5f3ff;
                    border-radius: 0 10px 10px 0;
                    color: #3730a3;
                    font-style: italic;
                  }
                  .audit-doc-v2 blockquote.intense {
                    border-color: #fecaca;
                    border-left-color: #ef4444;
                    background: #fef2f2;
                    color: #991b1b;
                  }
                  .audit-doc-v2 blockquote p {
                    color: inherit;
                    margin-bottom: 0.4rem;
                  }
                  .audit-doc-v2 blockquote p:last-child {
                    margin-bottom: 0;
                  }

                  /* ── Images ── */
                  .audit-doc-v2 img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 10px;
                    margin: 1.5rem auto;
                    display: block;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                  }

                  /* ── Links ── */
                  .audit-doc-v2 a {
                    color: #2563eb;
                    text-decoration: none;
                    border-bottom: 1px solid #bfdbfe;
                    transition: all 0.15s;
                  }
                  .audit-doc-v2 a:hover {
                    color: #1d4ed8;
                    border-bottom-color: #2563eb;
                  }

                  /* ── Horizontal rules ── */
                  .audit-doc-v2 hr {
                    border: none;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #cbd5e1 20%, #cbd5e1 80%, transparent);
                    margin: 2.5rem 0;
                  }

                  /* ── Print styles ── */
                  @media print {
                    .audit-doc-v2 { font-size: 12pt; }
                    .audit-doc-v2 table { page-break-inside: avoid; }
                    .audit-doc-v2 h1, .audit-doc-v2 h2, .audit-doc-v2 h3 { page-break-after: avoid; }
                  }
                `}</style>
                <AuditReportBody
                  report={selectedReport}
                  html={colorCodeSeverity(selectedReport.htmlContent)}
                  onReportChanged={() => handleViewReport(selectedReport.id)}
                />
              </div>

              {/* Document footer */}
              <div style={{
                borderTop: '1px solid #e2e8f0',
                padding: '16px 48px',
                background: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '18px', height: '18px', borderRadius: '4px',
                    background: 'linear-gradient(135deg, #3b82f6, #0ea5e9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Shield size={10} style={{ color: '#fff' }} />
                  </div>
                  <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 500 }}>
                    Gia Clinical Platform
                  </span>
                  <span style={{ color: '#cbd5e1', fontSize: '11px' }}>|</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>
                    Auditoria de Qualidade Clínica TEA
                  </span>
                </div>
                <span style={{ color: '#94a3b8', fontSize: '11px', fontFamily: 'monospace' }}>
                  {formatDate(selectedReport.created_at)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div style={{
              maxWidth: '860px', width: '100%', background: '#fff', borderRadius: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.08)',
              padding: '64px', textAlign: 'center'
            }}>
              <FileText size={56} style={{ color: '#cbd5e1', margin: '0 auto 16px auto' }} />
              <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>Pré-visualização não disponível.</p>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '6px' }}>Baixe o arquivo DOCX para ver o conteúdo.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ═══════════════════════════════════════════════════════════════════
          SECTION 1 — Documentos Clínicos (source documents for Gerar Auditoria)
          ═══════════════════════════════════════════════════════════════════ */}
      {canAudit && (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        {/* Section header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a, #334155)' }}>
              <FolderOpen size={16} style={{ color: '#38bdf8' }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Documentos Clínicos</h2>
              <p className="text-xs text-gray-400 mt-0.5">Laudos, relatórios e avaliações das disciplinas para auditoria</p>
            </div>
          </div>
          {clinicDocs.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {clinicDocs.length} {clinicDocs.length === 1 ? 'arquivo' : 'arquivos'}
              </span>
              <button
                onClick={handleStartAudit}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #0f172a, #1e40af)' }}
                disabled={runActive || uploadingDocs > 0}
                title="Analisar os documentos clínicos e gerar o relatório de auditoria"
              >
                {runActive ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />}
                {runActive ? 'Gerando auditoria...' : 'Gerar Auditoria'}
              </button>
            </div>
          )}
        </div>

        {/* Discipline filter chips */}
        {clinicDocs.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-50 flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedDiscipline('all')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
              style={{
                background: selectedDiscipline === 'all' ? '#0f172a' : '#f1f5f9',
                color: selectedDiscipline === 'all' ? '#fff' : '#64748b',
              }}
            >
              Todos
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{
                background: selectedDiscipline === 'all' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                color: selectedDiscipline === 'all' ? '#fff' : '#94a3b8',
              }}>
                {clinicDocs.length}
              </span>
            </button>
            {docCountByDiscipline.filter(d => d.count > 0).map(d => (
              <button
                key={d.key}
                onClick={() => setSelectedDiscipline(d.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  background: selectedDiscipline === d.key ? d.color : d.bg,
                  color: selectedDiscipline === d.key ? '#fff' : d.color,
                  border: `1px solid ${selectedDiscipline === d.key ? d.color : 'transparent'}`
                }}
              >
                <span>{d.icon}</span>
                {d.label}
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{
                  background: selectedDiscipline === d.key ? 'rgba(255,255,255,0.25)' : `${d.color}15`,
                  color: selectedDiscipline === d.key ? '#fff' : d.color
                }}>
                  {d.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Drag-and-drop zone */}
        <div className="p-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleClinicFileDrop}
            onClick={() => { if (!runActive) fileInputRef.current?.click(); }}
            className={`relative transition-all ${runActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            style={{
              border: `2px dashed ${dragOver ? '#3b82f6' : '#d1d5db'}`,
              borderRadius: '12px',
              padding: clinicDocs.length === 0 ? '40px 24px' : '20px 24px',
              background: dragOver ? '#eff6ff' : '#fafbfc',
              textAlign: 'center',
              transition: 'all 0.2s',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff,.pdf,.docx"
              onChange={handleClinicFileSelect}
              className="hidden"
            />

            {clinicDocs.length === 0 ? (
              <>
                <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{
                  background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                  border: '1px solid #bfdbfe'
                }}>
                  <Upload size={24} style={{ color: '#3b82f6' }} />
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Arraste os documentos clínicos aqui
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  ou clique para selecionar arquivos
                </p>
                <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1"><Image size={12} /> JPG, PNG, TIFF</span>
                  <span className="flex items-center gap-1"><File size={12} /> PDF, DOCX</span>
                </div>
                <p className="text-[11px] text-gray-300 mt-3">
                  Organize por pasta de disciplina: Médico, Fono, Psico, Psicoped, TO
                </p>
              </>
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Plus size={16} className="text-blue-500" />
                <span>Adicionar mais documentos</span>
              </div>
            )}
            {uploadingDocs > 0 && (
              <div className="flex items-center justify-center gap-2 text-xs text-blue-600 mt-3">
                <Loader2 size={12} className="animate-spin" />
                Enviando {uploadingDocs} {uploadingDocs === 1 ? 'arquivo' : 'arquivos'}...
              </div>
            )}
          </div>

          {/* Uploaded documents grid */}
          {filteredDocs.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredDocs.map((doc) => {
                const disc = DISCIPLINES.find(d => d.key === doc.discipline) || DISCIPLINES[5];
                return (
                  <div
                    key={doc.id}
                    className="group relative rounded-xl border border-gray-100 bg-white hover:shadow-md transition-all overflow-hidden"
                    style={{ borderLeft: `3px solid ${disc.color}` }}
                  >
                    {/* Icon area */}
                    {(
                      <div className="h-20 flex items-center justify-center" style={{ background: disc.bg }}>
                        {getFileIcon(doc.originalFilename) === 'image' ? (
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Image size={20} className="text-emerald-600" />
                          </div>
                        ) : getFileIcon(doc.originalFilename) === 'pdf' ? (
                          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <FileText size={20} className="text-red-500" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <File size={20} className="text-blue-500" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Info */}
                    <div className="p-3">
                      <p className="text-xs font-medium text-gray-800 truncate" title={doc.originalFilename}>{doc.originalFilename}</p>
                      <div className="flex items-center justify-between mt-2">
                        <select
                          value={doc.discipline}
                          onChange={(e) => updateDocDiscipline(doc.id, e.target.value)}
                          className="text-[11px] font-medium rounded-md border-0 py-0.5 px-1.5 cursor-pointer focus:ring-1 focus:ring-blue-300"
                          style={{ background: disc.bg, color: disc.color }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={runActive}
                        >
                          {DISCIPLINES.map(d => (
                            <option key={d.key} value={d.key}>{d.icon} {d.label}</option>
                          ))}
                        </select>
                        <span className="text-[10px] text-gray-400">{formatFileSize(doc.sizeBytes)}</span>
                      </div>
                    </div>

                    {/* Remove button */}
                    {!runActive && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeClinicDoc(doc.id); }}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
                    >
                      <X size={12} />
                    </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Audit run progress / outcome */}
          {auditRun && runActive && (
            <div className="mt-4 px-4 py-4 rounded-lg" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <div className="flex items-center gap-2 mb-3">
                <Loader2 size={14} className="animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Gerando auditoria — isso pode levar alguns minutos</span>
              </div>
              <ol className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {RUN_STAGES.map((stage, i) => {
                  const current = RUN_STAGES.findIndex(st => st.key === auditRun.stage);
                  const state = auditRun.status === 'queued' ? 'todo' : i < current ? 'done' : i === current ? 'doing' : 'todo';
                  return (
                    <li key={stage.key} className="flex items-center gap-1.5 text-xs" style={{ color: state === 'todo' ? '#94a3b8' : '#1e3a8a' }}>
                      {state === 'done' ? <CheckCircle2 size={13} className="text-green-600" />
                        : state === 'doing' ? <Loader2 size={13} className="animate-spin text-blue-600" />
                        : <span className="w-3 h-3 rounded-full border border-gray-300 inline-block" />}
                      <span className={state === 'doing' ? 'font-semibold' : ''}>
                        {stage.label}
                        {state === 'doing' && auditRun.progressTotal > 1 ? ` (${auditRun.progressDone}/${auditRun.progressTotal})` : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {auditRun && auditRun.status === 'failed' && (
            <div className="mt-4 flex items-center justify-between px-4 py-3 rounded-lg bg-red-50 border border-red-200">
              <div className="flex items-center gap-2">
                <AlertCircle size={14} className="text-red-500" />
                <span className="text-xs text-red-700">A última auditoria não foi concluída: {auditRun.errorMessage || 'erro desconhecido'}</span>
              </div>
              <button onClick={handleStartAudit} className="text-xs font-medium text-red-700 hover:underline whitespace-nowrap ml-3">
                Tentar novamente
              </button>
            </div>
          )}

          {clinicDocs.length > 0 && !runActive && (
            <div className="mt-4 flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div className="flex items-center gap-2">
                <Sparkles size={14} style={{ color: '#3b82f6' }} />
                <span className="text-xs text-gray-500">
                  Confira a disciplina de cada documento e clique em <strong>Gerar Auditoria</strong>. O GiA analisa todos os documentos juntos e o relatório aparece abaixo, pendente de validação.
                </span>
              </div>
              <button
                onClick={removeAllClinicDocs}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors whitespace-nowrap ml-3"
              >
                Limpar todos
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          SECTION 2 — Relatórios de Auditoria (existing audit reports)
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        {/* Section header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
              <CheckCircle2 size={16} style={{ color: '#fff' }} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Relatórios de Auditoria</h2>
              <p className="text-xs text-gray-400 mt-0.5">Relatórios QA gerados pelo GiA a partir dos documentos clínicos</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center mb-4">
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)}><X size={16} /></button>
            </div>
          )}

          {/* Reports List */}
          {loading ? (
            <div className="text-center text-gray-400 py-8 text-sm">
              Carregando relatórios...
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <FileText size={24} style={{ color: '#86efac' }} />
              </div>
              <p className="text-sm text-gray-500">Nenhum relatório de auditoria encontrado.</p>
              {canAudit && (
                <p className="text-xs text-gray-400 mt-1">Envie os documentos clínicos acima e clique em "Gerar Auditoria".</p>
              )}
            </div>
          ) : (
            <div className="space-y-2.5">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="group rounded-xl border border-gray-100 p-4 flex items-center justify-between hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => handleViewReport(report.id)}
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
                        <Shield size={18} style={{ color: '#3b82f6' }} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">{report.title}</h4>
                        {report.source === 'ai_generated' && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                            <Sparkles size={10} /> Gerado pelo GiA
                          </span>
                        )}
                        {report.source === 'ai_generated' && (report.validatedAt ? (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-100">
                            <BadgeCheck size={10} /> Validado{report.validator ? ` por ${report.validator.firstName}` : ''}
                          </span>
                        ) : (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                            <AlertCircle size={10} /> Pendente de validação
                          </span>
                        ))}
                        {report.openCommentCount > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-700 border border-slate-200" title="Comentários em aberto">
                            <MessageSquare size={10} /> {report.openCommentCount}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(report.reportDate)}</span>
                        <span className="truncate">{report.originalFilename}</span>
                        {report.uploader && (
                          <span className="flex items-center gap-1"><User size={11} />{report.uploader.firstName} {report.uploader.lastName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1.5 ml-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleViewReport(report.id)}
                      className="p-2 text-gray-300 hover:text-brand-600 rounded-lg hover:bg-brand-50 transition-colors"
                      title="Visualizar"
                      disabled={loadingReport}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleDownload(report)}
                      className="p-2 text-gray-300 hover:text-brand-600 rounded-lg hover:bg-brand-50 transition-colors"
                      title="Baixar"
                    >
                      <Download size={16} />
                    </button>
                    {canAudit && (
                    <button
                      onClick={() => setDeleteConfirm(report.id)}
                      className="p-2 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 border border-gray-100">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center mb-2">Confirmar Exclusão</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditReportsTab;
